import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const WEB3FORMS_KEY = '8587c2a7-32a5-44c3-afd8-02abb0eaf7d8';

const COUNTRY_CODES = [
  { code: '+44', flag: 'GB', label: 'United Kingdom' },
  { code: '+49', flag: 'DE', label: 'Germany' },
  { code: '+33', flag: 'FR', label: 'France' },
  { code: '+39', flag: 'IT', label: 'Italy' },
  { code: '+34', flag: 'ES', label: 'Spain' },
  { code: '+31', flag: 'NL', label: 'Netherlands' },
  { code: '+32', flag: 'BE', label: 'Belgium' },
  { code: '+41', flag: 'CH', label: 'Switzerland' },
  { code: '+43', flag: 'AT', label: 'Austria' },
  { code: '+46', flag: 'SE', label: 'Sweden' },
  { code: '+45', flag: 'DK', label: 'Denmark' },
  { code: '+47', flag: 'NO', label: 'Norway' },
  { code: '+358', flag: 'FI', label: 'Finland' },
  { code: '+353', flag: 'IE', label: 'Ireland' },
  { code: '+48', flag: 'PL', label: 'Poland' },
  { code: '+351', flag: 'PT', label: 'Portugal' },
  { code: '+1', flag: 'US', label: 'United States' },
];

const generateCaseId = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let id = 'CE-';
  for (let i = 0; i < 5; i++) id += chars.charAt(Math.floor(Math.random() * chars.length));
  return id;
};

const ContactModal = ({ isOpen, onClose, tier = '' }) => {
  const [titlePrefix, setTitlePrefix] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+44');
  const [phone, setPhone] = useState('');
  const [employees, setEmployees] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [caseId, setCaseId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) { setSubmitted(false); setError(''); setCaseId(''); }
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape' && !submitting) onClose(); };
    if (isOpen) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose, submitting]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    const newCaseId = generateCaseId();
    const fullName = [titlePrefix, firstName, lastName].filter(Boolean).join(' ');
    const fullPhone = `${countryCode} ${phone}`;
    const formData = {
      access_key: WEB3FORMS_KEY,
      subject: `ClearEqual Contact: ${tier || 'General'} inquiry from ${company} (${newCaseId})`,
      from_name: 'ClearEqual Contact Form',
      title_prefix: titlePrefix || '(none)',
      first_name: firstName,
      last_name: lastName,
      full_name: fullName,
      job_title: jobTitle,
      company,
      email,
      phone: fullPhone,
      employees: employees || 'Not specified',
      tier_of_interest: tier || 'Not specified',
      case_id: newCaseId,
      message: message || '(No message provided)',
    };
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(formData),
      });
      const result = await response.json();
      if (result.success) {
        setCaseId(newCaseId);
        setSubmitted(true);
        setTitlePrefix(''); setFirstName(''); setLastName(''); setJobTitle('');
        setCompany(''); setEmail(''); setPhone(''); setEmployees(''); setMessage('');
      } else {
        setError(result.message || 'Submission failed. Please try again or email contact@clearequal.com directly.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again, or email contact@clearequal.com directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => !submitting && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-2xl font-bold text-foreground">{submitted ? 'Inquiry received' : 'Contact us'}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {submitted ? 'We will respond within one business day.' : tier ? `${tier} tier inquiry` : 'We respond within one business day.'}
            </p>
          </div>
          <button onClick={onClose} disabled={submitting} className="p-2 hover:bg-muted rounded-md transition-smooth disabled:opacity-50" aria-label="Close">
            <Icon name="X" size={20} />
          </button>
        </div>

        {submitted ? (
          <div className="p-8 text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-success/10 rounded-full flex items-center justify-center">
              <Icon name="Check" size={32} className="text-success" />
            </div>
            <p className="text-foreground">Thank you. Your inquiry has been received.</p>
            <div className="bg-muted rounded-lg p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Reference number</p>
              <p className="text-2xl font-mono font-bold text-foreground tracking-wide">{caseId}</p>
              <p className="text-xs text-muted-foreground mt-2">Quote this in any follow-up correspondence.</p>
            </div>
            <button onClick={onClose} className="w-full mt-2 px-6 py-2.5 bg-primary text-white rounded-md font-semibold hover:bg-primary/90 transition-smooth">Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-foreground mb-1.5">Title</label>
                <select value={titlePrefix} onChange={(e) => setTitlePrefix(e.target.value)} disabled={submitting} className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white disabled:opacity-60">
                  <option value="">None</option>
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Ms">Ms</option>
                  <option value="Dr">Dr</option>
                  <option value="Prof">Prof</option>
                </select>
              </div>
              <div className="sm:col-span-4">
                <label className="block text-sm font-medium text-foreground mb-1.5">First name <span className="text-destructive">*</span></label>
                <input type="text" required value={firstName} onChange={(e) => setFirstName(e.target.value)} disabled={submitting} className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:opacity-60" placeholder="First name" />
              </div>
              <div className="sm:col-span-5">
                <label className="block text-sm font-medium text-foreground mb-1.5">Last name <span className="text-destructive">*</span></label>
                <input type="text" required value={lastName} onChange={(e) => setLastName(e.target.value)} disabled={submitting} className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:opacity-60" placeholder="Last name" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Job title <span className="text-destructive">*</span></label>
                <input type="text" required value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} disabled={submitting} className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:opacity-60" placeholder="e.g. CHRO, HR Director" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Company <span className="text-destructive">*</span></label>
                <input type="text" required value={company} onChange={(e) => setCompany(e.target.value)} disabled={submitting} className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:opacity-60" placeholder="Company name" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Work email <span className="text-destructive">*</span></label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} disabled={submitting} className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:opacity-60" placeholder="you@company.com" />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Phone <span className="text-destructive">*</span></label>
              <div className="flex gap-2">
                <select value={countryCode} onChange={(e) => setCountryCode(e.target.value)} disabled={submitting} className="px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white disabled:opacity-60 min-w-[110px]">
                  {COUNTRY_CODES.map((c) => (<option key={c.flag} value={c.code}>{c.flag} {c.code}</option>))}
                </select>
                <input type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} disabled={submitting} className="flex-1 px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:opacity-60" placeholder="20 1234 5678" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Approximate employees</label>
              <select value={employees} onChange={(e) => setEmployees(e.target.value)} disabled={submitting} className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white disabled:opacity-60">
                <option value="">Select range</option>
                <option value="Up to 250">Up to 250</option>
                <option value="250 to 1,000">250 to 1,000</option>
                <option value="1,000 to 5,000">1,000 to 5,000</option>
                <option value="5,000+">5,000+</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Message</label>
              <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} disabled={submitting} className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none disabled:opacity-60" placeholder="Tell us about your timeline, current setup, or any specific questions." />
            </div>

            {error && (
              <div className="bg-destructive/10 border border-destructive/30 rounded-md p-3">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <div className="flex items-center justify-end space-x-3 pt-2">
              <button type="button" onClick={onClose} disabled={submitting} className="px-5 py-2.5 text-foreground hover:bg-muted rounded-md font-medium transition-smooth disabled:opacity-50">Cancel</button>
              <button type="submit" disabled={submitting} className="px-6 py-2.5 bg-primary text-white rounded-md font-semibold hover:bg-primary/90 transition-smooth disabled:opacity-50 flex items-center space-x-2">
                {submitting && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                    <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" className="opacity-75" />
                  </svg>
                )}
                <span>{submitting ? 'Sending...' : 'Send'}</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactModal;
